import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { SiteHeader, SiteFooter, SourcewiseBadge, SOURCEWISE_URL } from "../components/Site";
import { usePageMeta } from "../lib/usePageMeta";

const ext = { target: "_blank", rel: "noopener noreferrer" };
const A = ({ href, children, testid }) => (
  <a href={href} {...ext} data-testid={testid} className="text-hf-green underline decoration-hf-line decoration-2 underline-offset-4 hover:decoration-hf-green">
    {children}
  </a>
);

const Section = ({ eyebrow, children, testid }) => (
  <section data-testid={testid} className="border-t-[1.5px] border-hf-line py-14 sm:py-16">
    <p className="hf-eyebrow">{eyebrow}</p>
    <div className="mt-5 space-y-5 text-[17px] leading-[1.75] text-hf-ink sm:text-lg">{children}</div>
  </section>
);

const STATS = [
  ["63M", "Americans, nearly 1 in 4 adults, provide unpaid care for a loved one."],
  ["85%", "of family caregivers never use formal respite services."],
  ["2030", "1 in 6 people worldwide will be 60 or older, and their care will land where it always has: on family."],
];

const SOURCES = [
  ["Utz, J Am Med Dir Assoc, 2021", "https://pmc.ncbi.nlm.nih.gov/articles/PMC9052870/"],
  ["ACL, 2022 National Strategy to Support Family Caregivers", "https://acl.gov/programs/support-caregivers/2022-national-strategy-support-family-caregivers"],
  ["WHO, Ageing and health", "https://www.who.int/news-room/fact-sheets/detail/ageing-and-health"],
];

function FounderPhoto() {
  return (
    <figure data-testid="founder-photo" className="mt-12">
      <img
        src="/img/joel-and-lea.webp"
        alt="Joël and his mom, Lea, smiling at a table at Max's, a Filipino restaurant"
        className="aspect-[1040/812] w-full rounded-xl border-[1.5px] border-hf-line object-cover"
      />
      <figcaption data-testid="founder-photo-caption" className="mt-3 text-[14px] italic text-hf-muted">
        Joël and his mom, Lea, at Max's, a Filipino restaurant.
      </figcaption>
    </figure>
  );
}

export default function Story() {
  usePageMeta("story", "/story");
  return (
    <div className="min-h-screen bg-hf-cream">
      <SiteHeader />
      <main className="mx-auto max-w-3xl px-5 pb-20 pt-8 sm:px-8 sm:pt-14">
        <div className="hf-rise">
          <p className="hf-eyebrow">Why it exists</p>
          <h1 data-testid="story-headline" className="mt-4 font-heading text-4xl leading-[1.1] sm:text-5xl lg:text-6xl">Built by a caregiver. Not a company.</h1>
          <p className="mt-6 text-lg leading-relaxed text-hf-muted sm:text-xl">
            The context the demo alone can't carry: why respite fails, and what Handoff does about it.
          </p>
          <div className="mt-6"><SourcewiseBadge testid="story-sourcewise-badge-top" /></div>
        </div>

        <FounderPhoto />

        <Section eyebrow="About the founder" testid="story-founder">
          <p>I'm Joël, a UX designer who became, almost overnight, a full-time family caregiver for both of my parents. My mom, Lea, is living with advanced cancer; my dad's needs are entirely different. Two people, two conditions, one home. For nearly two years, I didn't take a single real break, because when someone's life depends on the details, you become the one person who can never step away.</p>
          <p>I'm a first-generation college graduate and Filipino American, from a multigenerational Bay Area household, so I navigated doctors and paperwork for parents the system wasn't built for. For a lot of Filipino families, including mine, you don't send your parents somewhere. You become the somewhere.</p>
          <p>Every "you should take a break" ended the same way: it was easier to stay than to explain everything. As a designer, I did the only thing I knew how to do. I mapped a full shift from both sides, and found it breaks in the same place every time.</p>
          <p className="font-heading text-2xl leading-snug text-hf-green">Not the routine. The exceptions.</p>
          <p>So I built the tool I needed: a way to hand someone what I know, in my own words, so help can actually help.</p>
          <p>Before Handoff, I won Grand Prize in the 2024 Long COVID Healthathon by HHS, an earlier, separate project. I hold a B.A. in Sociology from UC Berkeley and IHSS paramedical certification for the care I provide at home. Before design, I worked in education. Translating complicated things into plain language is the oldest skill I have, and it's the same work Handoff does for a caregiver.</p>
        </Section>

        <Section eyebrow="The problem" testid="story-problem">
          <p>Most family caregivers don't lack helping hands. They lack a way to hand off what lives only in their heads: which medication at which time, the early sign of agitation, the fact that today is different because she barely slept. That transfer is hard, rushed, and almost always unplanned, so the break that was available goes unused.</p>
          <div className="grid gap-4 pt-4 sm:grid-cols-3">
            {STATS.map(([n, d], i) => (
              <div key={n} data-testid={`story-stat-${i + 1}`} className="hf-card p-5">
                <p className="font-heading text-5xl leading-none text-hf-green">{n}</p>
                <p className="mt-3 text-[15px] leading-relaxed text-hf-muted">{d}</p>
              </div>
            ))}
          </div>
          <blockquote data-testid="story-pull-quote" className="my-10 border-l-4 border-hf-coral py-2 pl-6">
            <p className="font-heading text-3xl leading-snug text-hf-ink sm:text-4xl">"It's easier to do it myself than to explain everything."</p>
            <footer className="mt-3 text-[15px] text-hf-muted">Why caregivers turn down the break they're offered.</footer>
          </blockquote>
          <p>Guilt and cost are real. The barrier no one has built for is trust.</p>
          <p>The routine hands off; the responsibility never does. Handoff treats trust as what it concretely is, an information-transfer problem, and solves that part with technology.</p>
          <p data-testid="story-sources" className="text-[14px] leading-relaxed text-hf-muted">
            Sources:{" "}
            {SOURCES.map(([label, href], i) => (
              <span key={href}>
                <A href={href} testid={`story-source-${i + 1}`}>{label}</A>
                {i < SOURCES.length - 1 ? "; " : "."}
              </span>
            ))}
          </p>
        </Section>

        <Section eyebrow="What Handoff does" testid="story-what">
          <p>Handoff turns a few minutes of speaking or typing into a clear, caregiver-approved brief for whoever is stepping in. The AI structures a tired stream of consciousness into a scannable page, pulls what's different today to the top, and prompts for the gaps a rushed caregiver leaves. Because our house holds two different conditions, Handoff was built diagnosis-agnostic from the first sketch.</p>
          <p>It never asserts a medical fact of its own. Nothing is shared until the caregiver reviews and approves. The brief arrives as a no-install web link on an ordinary phone.</p>
        </Section>

        <Section eyebrow="What's next" testid="story-next">
          <p>Respite changed my life. Through a caregiver respite grant from Sourcewise, I finally got one hour a day back. I used it to build Handoff, and it won first place out of 206 projects at the AI Squads HelpBnk Accelerator in July 2026.</p>
          <p>That taught me something: respite funding already exists, but most families never use it. They don't know it's there, and the people they actually trust are never part of the system.</p>
          <p>So Handoff is growing. Families can check what respite funding they may qualify for, invite someone they already trust to become their paid respite helper, and make every visit clear with a written agreement, a brief before, and a report after. Families pay nothing. Funding programs pay for verified visits. Helpers get paid.</p>
          <p>Handoff is a pilot, and we're looking for respite programs, health plans, and licensed providers to build it with us.</p>
          <div data-testid="story-sourcewise-feature" className="hf-card mt-6">
            <p className="text-[13px] font-semibold uppercase tracking-[0.12em] text-hf-muted">As featured by Sourcewise</p>
            <a href={SOURCEWISE_URL} {...ext} data-testid="story-sourcewise-article-link" className="mt-2 block font-heading text-2xl leading-snug text-hf-green hover:underline hover:underline-offset-4">
              "How Respite Helped a Family Caregiver Find His Creative Spark Again"
            </a>
          </div>
        </Section>

        <section className="border-t-[1.5px] border-hf-line pt-14 text-center">
          <p data-testid="story-closing" className="font-heading text-3xl sm:text-4xl">Hand off the care, not the worry.</p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row sm:flex-wrap">
            <Link to="/film" data-testid="story-cta-film" className="hf-btn hf-btn-primary">Watch the film</Link>
            <Link to="/demo" data-testid="story-cta-demo" className="hf-btn hf-btn-ghost hf-arrow">See the demo</Link>
            <Link to="/check" data-testid="story-cta-check" className="hf-btn hf-btn-ghost">Check if your break is covered <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
